import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import router from '@/router'
import type { ITask, ITaskMin } from '@/types/Task'

/*
 * THE TASK DRAWER, OPENED FROM ANYWHERE.
 *
 * Which task is open lives in the address bar — `?task=<id>` on whatever page is showing —
 * so a task can be sent to somebody, a reload keeps it open, and the link every e-mail
 * carries (`/projects/<project>?task=<task>`, docs/API.md → Notifications) opens it with no
 * code of its own. `open` and `close` only rewrite that query, with `replace`, so the back
 * button leaves the page rather than stepping through every task somebody looked at.
 *
 * The drawer is one component, mounted once by App.vue; the pages under it learn that
 * something changed through `version`, which goes up after every successful write the
 * drawer makes, and `lastChange`, which says what it was — a page that holds the task in a
 * list can patch it in place instead of loading everything again.
 */
export interface TaskChange {
    /** The task as the server answered after the write; absent for a deletion. */
    task?:      ITask
    /** The task that was deleted. */
    removedId?: string
    /** Successors the cascade moved. */
    shifted:    ITaskMin[]
}

export const useTaskDrawerStore = defineStore('taskDrawer', () => {
    const version    = ref(0)
    const lastChange = ref<TaskChange>()

    const taskId = computed((): string => {
        const value = router.currentRoute.value.query.task

        return typeof value === 'string' ? value : ''
    })

    const open = (id: string) => {
        const current = router.currentRoute.value

        if (current.query.task === id) return

        void router.replace({ query: { ...current.query, task: id } })
    }

    const close = () => {
        const current = router.currentRoute.value

        if (!('task' in current.query)) return

        const query = { ...current.query }
        delete query.task

        void router.replace({ query })
    }

    const notify = (change: TaskChange) => {
        lastChange.value = change
        version.value++
    }

    return { taskId, version, lastChange, open, close, notify }
})
